/**
 * What reading a file left out, said when it is read and not only at the
 * import's end (Decided 7 of docs/plans/files-paging-and-fixes.md): the pages
 * past those read, a workbook's sheets past those read, the columns left out
 * as sensitive, and the sheets OS is not sure of. One line under the file's
 * found line, and the same line in What OS knows › Files.
 *
 * Pure: the parts are the summary's own numbers and names, in the order the
 * line says them.
 */
import { sureFills, type FileSummary } from './agent-files';
import { t } from './i18n/t';

/**
 * The tabs a workbook is read to: the server's SHEETS_MAX (read-xlsx.ts),
 * held to it by read-xlsx.test.ts. A summary with sheets skipped read exactly
 * this many tabs, though a tab with no rows and no header is not among its
 * `sheets`, so the count read is this and never `sheets.length`.
 */
export const WORKBOOK_SHEETS_READ = 20;

/**
 * The most sheets past those read a summary names: the server's
 * SHEETS_SKIPPED_NAMED (read-xlsx.ts). A list this long may be cut, so the
 * workbook has at least the sheets read and those named, and maybe more.
 */
export const WORKBOOK_SHEETS_NAMED = 100;

/** Whether a workbook may have sheets past those its summary names as skipped: the list is as long as the server keeps. */
export const sheetsSkippedMayBeMore = (summary: FileSummary): boolean => (summary.sheetsSkipped ?? []).length >= WORKBOOK_SHEETS_NAMED;

/** One thing a reading left out. */
export type LeftOutPart =
  /** Pages past those read: a scan's are read at import, a text PDF's never. */
  | { readonly kind: 'pages'; readonly read: number; readonly total: number; readonly scan: boolean }
  /** A workbook's sheets past those read, by name; with `more`, `total` is a floor, since the names may be cut. */
  | { readonly kind: 'sheets'; readonly read: number; readonly total: number; readonly names: readonly string[]; readonly more: boolean }
  /** Columns left out because they look like account, card or Aadhaar numbers, across the sheets. */
  | { readonly kind: 'columns'; readonly names: readonly string[] }
  /** Sheets the decision model was not sure of: a question for the person, never a fact. */
  | { readonly kind: 'unsure'; readonly sheets: readonly string[] };

/** What a reading of this file left out, in the order the line says it; empty when nothing was. */
export const leftOutParts = (summary: FileSummary): LeftOutPart[] => {
  const parts: LeftOutPart[] = [];
  const pagesRead = summary.pagesRead ?? summary.pages.length;
  if (summary.pagesTotal !== null && summary.pagesTotal > pagesRead) {
    parts.push({ kind: 'pages', read: pagesRead, total: summary.pagesTotal, scan: summary.scan });
  }
  const skipped = summary.sheetsSkipped ?? [];
  if (skipped.length > 0) {
    parts.push({ kind: 'sheets', read: WORKBOOK_SHEETS_READ, total: WORKBOOK_SHEETS_READ + skipped.length, names: skipped, more: sheetsSkippedMayBeMore(summary) });
  }
  const columns = [...new Set(summary.sheets.flatMap((sheet) => sheet.leftOut))];
  if (columns.length > 0) parts.push({ kind: 'columns', names: columns });
  const unsure = summary.sheets.filter((sheet) => sheet.fills !== undefined && sheet.fills.ask && sureFills(sheet) === null).map((sheet) => sheet.name);
  if (unsure.length > 0) parts.push({ kind: 'unsure', sheets: unsure });
  return parts;
};

/**
 * A workbook's sheets a reading did not read, and the sheets OS is not sure
 * of: what a setup source's findings keep of the left-out line
 * (`SourceFindings.sheetsLeft`), so the studio's sources say it as the files
 * do. Pages and columns have their own places there (`partly`, `leftOut`).
 */
export type SheetsLeftPart = Extract<LeftOutPart, { readonly kind: 'sheets' | 'unsure' }>;

/** The sheets part of what a reading of this file left out, in the line's order; empty when none. */
export const sheetsLeftOf = (summary: FileSummary): SheetsLeftPart[] =>
  leftOutParts(summary).filter((part): part is SheetsLeftPart => part.kind === 'sheets' || part.kind === 'unsure');

/** "Sheet2 and Sheet3". */
const namesAnd = (names: readonly string[]): string =>
  names.length < 2 ? (names[0] ?? '') : t('files.found.names_and', { names: names.slice(0, -1).join(', '), last: names.at(-1)! });

const partWords = (part: LeftOutPart): string => {
  switch (part.kind) {
    case 'pages':
      return part.scan ? t('files.left_out.pages_scan', { read: part.read, total: part.total }) : t('files.left_out.pages', { read: part.read, total: part.total });
    case 'sheets':
      return part.more ? t('files.left_out.sheets_more', { read: part.read, total: part.total }) : t('files.left_out.sheets', { read: part.read, total: part.total });
    case 'columns':
      return t('files.left_out.columns', { count: part.names.length, names: part.names.join(', ') });
    case 'unsure':
      return t('files.left_out.unsure', { count: part.sheets.length, names: namesAnd(part.sheets) });
  }
};

/** The parts as one line, "20 of 23 sheets read · Not sure what Sheet2 holds"; null when there are none. */
export const leftOutWords = (parts: readonly LeftOutPart[]): string | null => {
  const [first, ...rest] = parts;
  if (first === undefined) return null;
  return rest.reduce((line, part) => t('files.left_out.join', { first: line, rest: partWords(part) }), partWords(first));
};

/** What a reading of this file left out, in one line; null when nothing was. */
export const leftOutLine = (summary: FileSummary): string | null => leftOutWords(leftOutParts(summary));
